import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import { Coins, Copy, DollarSign } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import DrawerForm from "src/components/drawer-form/drawer-form";
import OrderCreateForm from "src/components/order-create-form/order-create-form";
import OrderDescCard from "src/components/order-desc-card/order-desc-card";
import OrderStatusLabel from "src/components/order-status/order-status";
import TimeLabel from "src/components/time-label/time-label";
import orderQueriers from "src/queries/order-queries";
import type { Order, OrderParams, OrderStatus } from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";
import { formatCryptoAmount } from "src/utils/format";
import {
	cryptoTickerOptions as CRYPTO_OPTIONS,
	fiatOptions as CURRENCY_OPTIONS,
} from "src/utils/options";
import { STATUS_CONFIG } from "src/utils/status-config";

const ORDER_STATUSES = [
	"pending",
	"paid",
	"partially_paid",
	"expired",
	"cancelled",
	"aml_rejected",
] as const;

const ORDER_STATUS_OPTIONS = ORDER_STATUSES.map((status) => {
	const config = STATUS_CONFIG[status];
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: ORDER_STATUS_OPTIONS,
	},
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <DollarSign className="h-4 w-4" />,
		options: CURRENCY_OPTIONS,
	},
	{
		id: "cryptoCurrency",
		label: "Crypto",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: CRYPTO_OPTIONS,
	},
];

const OrdersPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const page = pagination.page;
	const limit = pagination.limit;
	const setPage = (newPage: number) => setPagination({ page: newPage });
	const setLimit = (newLimit: number) =>
		setPagination({ limit: newLimit, page: 1 });

	// Convert FilterBuilder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: OrderStatus;
			currency?: string;
			cryptoCurrency?: string;
		} = {};

		for (const filter of filters) {
			if (filter.operator === "is" && filter.value) {
				if (filter.field === "status") {
					result.status = filter.value as OrderStatus;
				} else if (filter.field === "currency") {
					result.currency = filter.value;
				} else if (filter.field === "cryptoCurrency") {
					result.cryptoCurrency = filter.value;
				}
			}
		}

		return result;
	}, [filters]);

	const listOrders = orderQueriers.listOrders({
		status: apiFilters.status,
		search,
		currency: apiFilters.currency,
		cryptoCurrency: apiFilters.cryptoCurrency,
		limit,
		offset: (page - 1) * limit,
	});
	const createOrder = orderQueriers.createOrder();

	const [isFormOpen, setFormOpen] = React.useState<boolean>(false);
	const [openedCard, setOpenedCard] = React.useState<Order | null>(null);
	const [isOrderDrawerOpen, setIsOrderDrawerOpen] = React.useState(false);
	const orders = listOrders.data?.results ?? [];
	const totalCount = listOrders.data?.totalCount ?? 0;
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);

	const isLoading =
		listOrders.isLoading || createOrder.isPending || listOrders.isFetching;

	const openNotification = React.useCallback(
		(title: string, description: string) => {
			toast.success(title, {
				description,
			});
		},
		[],
	);

	const handleCreateOrder = async (values: OrderParams) => {
		try {
			setIsFormSubmitting(true);
			await createOrder.mutateAsync(values);
			setFormOpen(false);
			openNotification(
				"Order Created Successfully",
				"The order has been created and is ready to accept payment.",
			);
		} catch (error) {
			console.error("Error creating order:", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const handleFormFailed = () => {
		toast.error("Validation Failed", {
			description: "Please check the form fields",
		});
	};

	const handleViewOrder = React.useCallback((order: Order) => {
		setOpenedCard(order);
		setIsOrderDrawerOpen(true);
	}, []);

	const columns: ColumnDef<Order>[] = React.useMemo(
		() => [
			{
				id: "merchantOrderId",
				header: "Order ID",
				cell: (row) => (
					<button
						type="button"
						aria-label={`Copy order ID ${row.merchantOrderId}`}
						className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
						onClick={(e) => {
							e.stopPropagation();
							copyToClipboard(row.merchantOrderId, openNotification);
						}}
					>
						<span className="text-sm font-semibold">{row.merchantOrderId}</span>
						<Copy
							aria-hidden="true"
							className="h-4 w-4 text-muted-foreground hover:text-primary"
						/>
					</button>
				),
			},
			{
				id: "price",
				header: "Price",
				cell: (row) => (
					<span className="whitespace-nowrap font-mono">
						{row.price} {row.currency}
					</span>
				),
			},
			{
				id: "crypto",
				header: "Crypto",
				cell: (row) => (
					<CryptoIcon
						ticker={row.cryptoCurrency}
						showLabel
						className="h-6 w-6"
						showComposite={true}
					/>
				),
			},
			{
				id: "cryptoAmount",
				header: "Crypto Amount",
				cell: (row) => (
					<span className="text-sm font-mono">
						{formatCryptoAmount(row.cryptoAmount, {
							ticker: row.cryptoCurrency,
						})}
					</span>
				),
			},
			{
				id: "received",
				header: "Received",
				cell: (row) =>
					row.receivedAmount ? (
						<span className="text-sm font-mono">
							{formatCryptoAmount(row.receivedAmount, {
								ticker: row.cryptoCurrency,
							})}
						</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				id: "status",
				header: "Status",
				cell: (row) => <OrderStatusLabel status={row.status} />,
			},
			{
				id: "createdAt",
				header: "Created At",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
		],
		[openNotification],
	);

	const hasActiveFilters = search || filters.length > 0;

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<h1 className="sr-only">Orders</h1>

			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-3">
					<span aria-hidden className="h-5 w-[3px] rounded-full bg-primary" />
					<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
						Orders · Crypto payment requests
					</p>
				</div>
				<Button onClick={() => setFormOpen(true)}>New Order</Button>
			</div>

			<div className="flex gap-4 flex-wrap items-center">
				<SearchInput
					placeholder="Search orders..."
					className="w-full sm:w-[300px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={orders}
				isLoading={isLoading}
				onRowClick={handleViewOrder}
				getRowId={(row) => row.id}
				emptyMessage={hasActiveFilters ? "No orders found" : "No orders yet"}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your search criteria"
						: "Generate a unique deposit address per order to accept crypto payments via the API."
				}
				emptyAction={
					hasActiveFilters ? undefined : (
						<Button onClick={() => setFormOpen(true)}>
							Create your first order
						</Button>
					)
				}
				pagination={
					totalCount > 0
						? {
								page,
								pageSize: limit,
								totalCount,
								onPageChange: setPage,
								onPageSizeChange: setLimit,
							}
						: undefined
				}
			/>

			<DrawerForm
				title="Order details"
				isOpen={isOrderDrawerOpen}
				onClose={() => setIsOrderDrawerOpen(false)}
				width={512}
			>
				{openedCard && (
					<OrderDescCard order={openedCard} onCopy={openNotification} />
				)}
			</DrawerForm>

			<DrawerForm
				title="Create order"
				description="Create a pre-configured crypto invoice where you define the exact currency."
				isOpen={isFormOpen}
				onClose={() => {
					setFormOpen(false);
				}}
				onSubmit={() => {
					const form = document.querySelector("form");
					if (form) {
						form.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={isFormSubmitting}
				submitText="Create Order"
				width={512}
			>
				<OrderCreateForm
					onFinish={handleCreateOrder}
					onFinishFailed={handleFormFailed}
					isSubmitting={isFormSubmitting}
				/>
			</DrawerForm>
		</div>
	);
};

export default OrdersPage;
